// libs
import moment from 'moment'
// types
import { FieldType, IField, IChartDataRow, ChartAggregate, ChartDateBucket } from 'types/graphql'
import { ChartType, ChartSortBy, ChartConfig, ChartSwitchKey, ChartSortOrder, ChartValueMode } from 'types/charts'
// constants
import { CREATED_AT_FIELD_ID } from 'constants/charts'
// utils
import { flatten, sumBy } from 'lodash'

export interface ChartCategory {
  id: string
  label: string
  color?: string
  isEmpty?: boolean
}

export interface ChartSeries {
  id: string
  name: string
  color?: string
  values: (number | null)[]
}

export interface ChartData {
  categories: ChartCategory[]
  series: ChartSeries[]
  isPercent: boolean
}

// order: where the key comes with Sort by NONE; labelOrder: with Sort by LABEL, when the label is not what sorts it
// (a date written as "Jan 2024", a number)
type KeyMeta = {
  id: string
  label: string
  color?: string
  isEmpty?: boolean
  order: string | number
  labelOrder?: string | number
}

// Ids that come from no key: the one series of a chart not split by a second field, the "Others" of Top N, and the one
// category of every key the app cannot name. Underscored so that no key of the data matches them: a tag title is any
// text, "others" included
const ALL_ID = '__all__'
const OTHERS_ID = '__others__'
const UNKNOWN_ID = '__unknown__'

// chartData's key of no value
const EMPTY_KEY = 'null'

// An option removed from the field comes after the ones it still has
const UNKNOWN_ORDER = Number.MAX_SAFE_INTEGER

// Removed options, deleted records and cells with no rating are one category: the viewer could not tell them apart
const getUnknownKey = (unknownLabel: string): KeyMeta => ({ id: UNKNOWN_ID, label: unknownLabel, order: UNKNOWN_ORDER })

const DATE_FORMATS: Record<ChartDateBucket, (date: moment.Moment) => string> = {
  [ChartDateBucket.Day]: (date) => date.format('D MMM YYYY'),
  [ChartDateBucket.Week]: (date) => `Wk ${date.format('D MMM YYYY')}`,
  [ChartDateBucket.Month]: (date) => date.format('MMM YYYY'),
  [ChartDateBucket.Quarter]: (date) => `Q${date.quarter()} ${date.format('YYYY')}`,
  [ChartDateBucket.Year]: (date) => date.format('YYYY')
}

// The rating of a risk matrix cell, and where it comes among the matrix's ratings
const getMatrixRating = (field: IField, cellId: string) => {
  const cells = flatten((field.settings?.bRightSection || []) as { id?: string; value?: string }[][])
  const cell = cells.find((item) => item?.id === cellId)
  const ratings = field.settings?.options || []
  const index = ratings.findIndex((option) => option.id === cell?.value)

  return index === -1 ? null : { rating: ratings[index], index }
}

// Joins values that land in one category (risk matrix cells of one rating, the "Others" group)
export const combineValues = (aggregate: ChartAggregate, values: (number | null)[]): number | null => {
  const numbers = values.filter((value) => value !== null && value !== undefined)

  if (!numbers.length) {
    return null
  }

  switch (aggregate) {
    case ChartAggregate.Min:
      return Math.min(...numbers)
    case ChartAggregate.Max:
      return Math.max(...numbers)
    case ChartAggregate.Avg:
      return sumBy(numbers) / numbers.length
    default:
      return sumBy(numbers)
  }
}

const describeKey = (
  key: string | null,
  label: string | null,
  field: IField | undefined,
  fieldId: string,
  dateBucket: ChartDateBucket | undefined,
  config: ChartConfig
): KeyMeta => {
  const { emptyLabel, unknownLabel } = config.transform

  if (key === null || key === undefined) {
    return { id: EMPTY_KEY, label: emptyLabel, isEmpty: true, order: emptyLabel }
  }

  if (fieldId === CREATED_AT_FIELD_ID || field?.fieldType === FieldType.Date) {
    const date = moment(key, 'YYYY-MM-DD')

    return { id: key, label: DATE_FORMATS[dateBucket || ChartDateBucket.Month](date), order: key, labelOrder: key }
  }

  switch (field?.fieldType) {
    case FieldType.PickList: {
      // An option removed from the field since
      const index = (field.options || []).findIndex((item) => item.id === key)
      const option = field.options?.[index]

      return option
        ? { id: key, label: option.label || option.value || key, order: index }
        : getUnknownKey(unknownLabel)
    }
    case FieldType.Lookup: {
      // A record deleted since is missing from chartData.labels
      return label ? { id: key, label, order: label } : getUnknownKey(unknownLabel)
    }
    case FieldType.Switch: {
      const name = key === ChartSwitchKey.True ? 'Yes' : 'No'

      return { id: key, label: name, order: name }
    }
    case FieldType.RiskMatrix: {
      // Cells of one rating share a category: the rating is what a viewer reads
      const matrixRating = getMatrixRating(field, key)

      if (!matrixRating) {
        return getUnknownKey(unknownLabel)
      }

      const { rating, index } = matrixRating

      return {
        id: rating.id,
        label: rating.label,
        color: config.display.colors.useFieldColors ? rating.color : undefined,
        order: index
      }
    }
    case FieldType.Number: {
      return { id: key, label: key, order: Number(key), labelOrder: Number(key) }
    }
    default: {
      return { id: key, label: label || key, order: label || key }
    }
  }
}

const compareValues = (a: string | number, b: string | number) => {
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b
  }

  return String(a).localeCompare(String(b), undefined, { numeric: true })
}

const getLabelOrder = (key: KeyMeta) => key.labelOrder ?? key.label

// "No value" comes last whatever the sort, a descending one too
const emptyLast = (compare: (a: KeyMeta, b: KeyMeta) => number) => (a: KeyMeta, b: KeyMeta) => {
  if (a.isEmpty !== b.isEmpty) {
    return a.isEmpty ? 1 : -1
  }

  return compare(a, b)
}

// The rows come in no order: NONE is the keys' own order, LABEL their names; direction -1 sorts descending
const compareKeys = (by: 'order' | 'label', direction = 1) =>
  emptyLast(
    (a, b) =>
      direction * (by === 'order' ? compareValues(a.order, b.order) : compareValues(getLabelOrder(a), getLabelOrder(b)))
  )

export const prepareChartData = (
  rows: IChartDataRow[],
  lookupLabels: Record<string, string>,
  config: ChartConfig,
  fields: Record<string, IField>,
  valueName: string
): ChartData => {
  const { query, transform, type } = config
  const [categoryGroup, seriesGroup] = query.groupBy
  const isAxisChart = type !== ChartType.Pie

  const categories = new Map<string, KeyMeta>()
  const series = new Map<string, KeyMeta>()
  const cells = new Map<string, (number | null)[]>()

  rows.forEach((row) => {
    // Every chart type groups by one field at least (GROUP_BY_COUNTS)
    const category = describeKey(
      row.keys[0],
      lookupLabels[row.keys[0]] ?? null,
      fields[categoryGroup.fieldId],
      categoryGroup.fieldId,
      categoryGroup.dateBucket,
      config
    )

    const serie =
      seriesGroup && isAxisChart
        ? describeKey(
            row.keys[1],
            lookupLabels[row.keys[1]] ?? null,
            fields[seriesGroup.fieldId],
            seriesGroup.fieldId,
            seriesGroup.dateBucket,
            config
          )
        : { id: ALL_ID, label: valueName, order: 0 }

    if (transform.hideEmpty && (category.isEmpty || serie.isEmpty)) {
      return
    }

    categories.set(category.id, category)
    series.set(serie.id, serie)

    const cellId = `${category.id}\u0000${serie.id}`
    cells.set(cellId, [...(cells.get(cellId) || []), row.value])
  })

  const getValue = (categoryId: string, serieId: string) =>
    combineValues(query.aggregate, cells.get(`${categoryId}\u0000${serieId}`) || [])

  const seriesList = [...series.values()].sort(compareKeys('order'))

  const categoryTotal = (category: KeyMeta) => sumBy(seriesList, (serie) => getValue(category.id, serie.id) || 0)

  let categoryList = [...categories.values()]

  const direction = transform.sortBy !== ChartSortBy.None && transform.sortOrder === ChartSortOrder.Desc ? -1 : 1

  if (transform.sortBy === ChartSortBy.Value) {
    categoryList.sort(emptyLast((a, b) => direction * (categoryTotal(a) - categoryTotal(b))))
  } else {
    categoryList.sort(compareKeys(transform.sortBy === ChartSortBy.Label ? 'label' : 'order', direction))
  }

  let othersValues: (number | null)[] | null = null

  if (transform.topN && categoryList.length > transform.topN) {
    const rest = categoryList.slice(transform.topN)
    categoryList = categoryList.slice(0, transform.topN)

    if (transform.groupOthers) {
      othersValues = seriesList.map((serie) =>
        combineValues(
          query.aggregate,
          rest.map((category) => getValue(category.id, serie.id))
        )
      )
    }
  }

  const resultCategories: ChartCategory[] = categoryList.map(({ id, label, color, isEmpty }) => ({
    id,
    label,
    color,
    isEmpty
  }))
  if (othersValues) {
    resultCategories.push({ id: OTHERS_ID, label: transform.othersLabel })
  }

  const seriesValues = seriesList.map((serie, serieIndex) => {
    const values = categoryList.map((category) => getValue(category.id, serie.id))

    return othersValues ? [...values, othersValues[serieIndex]] : values
  })

  // Shares of everything drawn, so stacked series add up to 100% in all
  const grandTotal = sumBy(seriesValues, (values) => sumBy(values, (value) => value || 0))

  const resultSeries: ChartSeries[] = seriesList.map((serie, serieIndex) => {
    let values = seriesValues[serieIndex]

    if (transform.valueMode === ChartValueMode.Percent) {
      values = values.map((value) => (value === null || !grandTotal ? value : (value / grandTotal) * 100))
    }

    if (transform.valueMode === ChartValueMode.Cumulative) {
      let running = 0
      values = values.map((value) => (running += value || 0))
    }

    return { id: serie.id, name: serie.label, color: serie.color, values }
  })

  return {
    categories: resultCategories,
    series: resultSeries,
    isPercent: transform.valueMode === ChartValueMode.Percent
  }
}
